import moment from 'moment';
import { useRouter } from 'next/router';
import { getConfigWithNoThirdPartySupport } from 'Components/third-party/thirdParty.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { getDataLayer } from 'Tracking/tracking-api';
import {
  setInSessionStorage,
  getFromSessionStorage,
} from 'Client/session-storage-api/sessionStorageApi';
import { KEY_BE_ABTASTY_EVENTS } from 'Client/session-storage-api/sessionStorage.constants';
export type ABTastyTransactionParams = {
  id: string;
  affiliation: string;
  revenue: number;
  currency: string;
};

export type ABTastyItemParams = {
  id: string;
  name: string;
  price: number;
  code: string;
  category: string;
};

type AbTastyScriptClient = {
  send?: (event: string, data: Record<string, unknown>) => void;
};

type DataLayerEvent = {
  event?: string;
  [key: string]: unknown;
};

const getAbTastyClient = (): AbTastyScriptClient | undefined =>
  window.abtasty as AbTastyScriptClient | undefined;

export const sendTransaction = ({
  id,
  affiliation,
  revenue,
  currency,
}: ABTastyTransactionParams) => {
  const timeLimit = moment().add(5, 's');
  const waiter = setInterval(() => {
    const abtastyClient = getAbTastyClient();
    if (abtastyClient?.send) {
      abtastyClient.send('transaction', {
        tid: id,
        // Transaction ID
        ta: affiliation,
        // Transaction Affiliation
        tr: revenue,
        // Transaction Revenue
        tc: currency, // Transaction Currency
        icn: 1, // Number of items
      });
      clearInterval(waiter);
    } else if (moment().isAfter(timeLimit)) {
      // clear after Maximum 5s, we assume that it should be there by then
      clearInterval(waiter);
    }
  }, 100);
};

export const sendItem = ({ id, name, price, code, category }: ABTastyItemParams) => {
  const timeLimit = moment().add(5, 's');
  const waiter = setInterval(() => {
    const abtastyClient = getAbTastyClient();
    if (abtastyClient?.send) {
      abtastyClient.send('item', {
        tid: id, // Transaction ID (links to transaction)
        in: name, // Resort name
        ip: price, // Resort price
        iq: 1, // Quantity (always 1)
        ic: code, // Resort code (SKU)
        iv: category, // Resort exclusive collection category
      });
      clearInterval(waiter);
    } else if (moment().isAfter(timeLimit)) {
      // clear after Maximum 5s, we assume that it should be there by then
      clearInterval(waiter);
    }
  }, 100);
};

const getABTastyId = (abtasty, { isCmta, isCxo }) => {
  if (isCmta && abtasty.cmta) {
    return abtasty.cmta.id;
  }

  if (isCxo && abtasty.cxo) {
    return abtasty.cxo.id;
  }

  return abtasty.id;
};

type AbTastyHook = {
  abTastyScriptUrl: string;
  isAbTastyActive: boolean;
};
export const useAbTasty = (): AbTastyHook => {
  const { isCmta, isCxo, locale } = useAppContext();
  const { query: queryParams } = useRouter();
  const { enabled, abtasty } = getConfigWithNoThirdPartySupport({
    locale,
    name: 'tracking',
    queryParams,
  });
  const abTastyId = getABTastyId(abtasty, {
    isCmta,
    isCxo,
  });
  const isAbTastyActive = Boolean(enabled && abtasty?.enabled && abtasty?.host && abTastyId);
  const abTastyScriptUrl = isAbTastyActive ? `https://${abtasty.host}/${abTastyId}.js` : '';
  return {
    abTastyScriptUrl,
    isAbTastyActive,
  };
};
export const storeAbTastyEvent = () => {
  const { google } = getDataLayer();
  const abTastyEventsToStore = {};
  const abTastyEvents = ((google ?? []) as DataLayerEvent[]).filter(
    (event) => event.event === 'ab_tasty',
  );
  abTastyEvents.forEach((abTastyEvent) => {
    for (const [key, value] of Object.entries(abTastyEvent)) {
      if (key.includes('ab_tasty')) {
        abTastyEventsToStore[key] = value;
      }
    }
  });
  setInSessionStorage(KEY_BE_ABTASTY_EVENTS, abTastyEventsToStore);
};
export const getAbTastyEvent = () => {
  return getFromSessionStorage(KEY_BE_ABTASTY_EVENTS) || {};
};
